import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, errMsg, fmt } from "../lib/api";
import { useAuth } from "../lib/auth";

function Kpi({ cls, icon, title, cn, value, sub, onClick, testid }) {
  return (
    <div className={`dashboard-kpi ${cls} ${onClick ? "clickable" : ""}`} onClick={onClick} data-testid={testid}>
      <div className="kpi-icon">{icon}</div>
      <div><small>{title}<br />{cn}</small><strong>{value}</strong><span>{sub}</span></div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [period, setPeriod] = useState("today");
  const [err, setErr] = useState("");
  const [all, setAll] = useState(false);
  const [openArea, setOpenArea] = useState("A1");
  const nav = useNavigate();
  const { user } = useAuth();
  const load = useCallback(async () => {
    try { setErr(""); setD((await api.get("/dashboard", { params: { period } })).data); } catch (e) { setErr(errMsg(e)); }
  }, [period]);
  useEffect(() => { load(); }, [load]);
  if (err) return <div className="alert error" data-testid="dashboard-error">DATA ERROR / 数据错误: {err}</div>;
  if (!d) return <div className="dashboard-loading" data-testid="dashboard-loading">Memuat dashboard... / 加载中...</div>;
  const daily = all ? d.daily : d.daily.slice(0, 10);
  const maxArea = Math.max(...d.areas.map((a) => a.ton), 0.001);
  return (
    <div className="dash" data-testid="dashboard-page">
      <div className="dashboard-hero">
        <div><h2>RINGKASAN OPERASIONAL GUDANG</h2><p>仓库运营概览 · Stock, transaksi, dan tindakan penting hari ini</p></div>
        <div className="hero-right">
          <span className="live-pill">SYSTEM NORMAL / 系统正常运行</span>
          <button className="dashboard-refresh" onClick={load} data-testid="dashboard-refresh-btn">REFRESH / 刷新</button>
        </div>
      </div>
      <div className="dashboard-kpi-grid six">
        <Kpi cls="kpi-blue" icon="▣" title="TOTAL STOCK" cn="库存总量" value={`${fmt(d.total.ton)} Ton`} sub={`${fmt(d.total.qty)} Unit / 个 • ${d.total.barcodes} Barcode`} onClick={() => nav("/stockdata")} testid="kpi-total-stock" />
        <Kpi cls="kpi-green" icon="↓" title="BARANG MASUK HARI INI" cn="今日入库" value={`${fmt(d.today.masuk.ton)} Ton`} sub={`${d.today.masuk.count} Transaksi • ${fmt(d.today.masuk.qty)} Qty`} testid="kpi-masuk-today" />
        <Kpi cls="kpi-red" icon="↑" title="BARANG KELUAR HARI INI" cn="今日出库" value={`${fmt(d.today.keluar.ton)} Ton`} sub={`${d.today.keluar.count} Transaksi • ${fmt(d.today.keluar.qty)} Qty`} testid="kpi-keluar-today" />
        <Kpi cls="kpi-blue" icon="▱" title="PLAT" cn="钢板" value={`${fmt(d.kategori.PLAT.ton)} Ton`} sub={`${fmt(d.kategori.PLAT.qty)} Lembar / 张`} testid="kpi-plat" />
        <Kpi cls="kpi-orange" icon="⌁" title="PROFILE" cn="型材" value={`${fmt(d.kategori.PROFILE.ton)} Ton`} sub={`${fmt(d.kategori.PROFILE.qty)} Batang / 根`} testid="kpi-profile" />
        <Kpi cls="kpi-violet" icon="◉" title="COIL" cn="卷板" value={`${fmt(d.kategori.COIL.qty)} Roll`} sub={d.kategori.COIL.ton ? `${fmt(d.kategori.COIL.ton)} Ton` : "Tonase belum tersedia / 吨位待补充"} testid="kpi-coil" />
      </div>
      <div className="dashboard-main-grid two">
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <div><h3>RINGKASAN PERIODE</h3><small>期间汇总 · Sumber: Tanggal Transaksi ledger</small></div>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="dashboard-period-select">
              <option value="today">Hari Ini / 今天</option><option value="week">Minggu Ini / 本周</option><option value="month">Bulan Ini / 本月</option>
            </select>
          </div>
          <div className="muted small">{d.period_start} s/d {d.period_end}</div>
          <div className="period-grid" data-testid="dashboard-period-summary">
            <div><span>Masuk / 入库</span><b>{fmt(d.periode.masuk.ton)} Ton</b><small>{d.periode.masuk.count} baris • {fmt(d.periode.masuk.qty)} qty</small></div>
            <div><span>Keluar / 出库</span><b>{fmt(d.periode.keluar.ton)} Ton</b><small>{d.periode.keluar.count} baris • {fmt(d.periode.keluar.qty)} qty</small></div>
          </div>
          {user.role === "ADMINISTRATOR" && d.total.rows_without_tonase > 0 && <div className="alert warning small">{d.total.rows_without_tonase} baris stok belum memiliki Tonase/Unit — total ton tidak mencakup baris ini.</div>}
        </div>
        <div className="dashboard-card action-card">
          <div className="dashboard-card-header"><div><h3 className="red">PERLU TINDAKAN</h3><small>待处理事项</small></div></div>
          <div className="action-grid">
            <button onClick={() => nav("/verifikasi")} data-testid="action-pending-verifikasi"><span>Verifikasi Pemindahan<small>移动验证</small></span><b>{d.pending_verifikasi}</b></button>
            <button onClick={() => nav("/opname")} data-testid="action-opname-selisih"><span>Stock Opname Selisih (belum adjust)<small>盘点差异</small></span><b>{d.opname_selisih}</b></button>
          </div>
        </div>
      </div>
      <div className="dashboard-card mt">
        <div className="dashboard-card-header">
          <div><h3>RIWAYAT TRANSAKSI HARIAN</h3><small>每日交易记录</small></div>
          <div className="hero-right"><span className="chip">TOTAL {d.daily.length} BARIS • MASUK {d.today.masuk.count} • KELUAR {d.today.keluar.count}</span>
            <button className="btn-mini" onClick={() => setAll(!all)} data-testid="dashboard-toggle-all">{all ? "RINGKAS" : "TAMPILKAN SEMUA / 显示全部"}</button></div>
        </div>
        <div className="table-wrap">
          <table className="data-table" data-testid="dashboard-daily-table">
            <thead><tr><th>Tanggal / Waktu</th><th>Jenis</th><th>Material</th><th>Tebal</th><th>Grade</th><th>Qty</th><th>Tonase</th><th>Vendor/Supplier</th><th>Area</th><th>Proyek</th><th>Referensi</th></tr></thead>
            <tbody>
              {daily.length === 0 && <tr><td colSpan="11" className="empty">Belum ada transaksi hari ini / 今日暂无交易</td></tr>}
              {daily.map((r, i) => (
                <tr key={i}><td>{String(r.ts).slice(0, 19).replace("T", " ")}</td><td><span className={`tag t-${r.aktivitas.split(" ")[1] || "x"}`}>{r.aktivitas}</span></td>
                  <td><b>{r.material_code}</b><br /><small>{r.barcode_id}</small></td><td>{r.tebal || ""}</td><td>{r.grade_label && <span className="grade-chip">{r.grade_label}</span>}</td><td>{fmt(r.qty)} {r.satuan}</td><td>{fmt(r.total_kg / 1000)} t</td>
                  <td>{r.vendor || r.supplier}</td><td>{r.area}</td><td>{r.proyek}</td><td>{r.ref}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="dashboard-card mt">
        <div className="dashboard-card-header"><div><h3>STOCK PER AREA</h3><small>按区域库存 · Klik area untuk buka/tutup detail</small></div><button className="dashboard-refresh" onClick={load} data-testid="area-refresh-btn">REFRESH / 刷新</button></div>
        <div className="area-stack" data-testid="dashboard-area-detail">
          {d.areas.map((a) => { const rows = d.area_detail?.[a.area] || []; const open = openArea === a.area; return (
            <div className="area-card" key={a.area}>
              <button className="area-head" onClick={() => setOpenArea(open ? "" : a.area)} data-testid={`area-toggle-${a.area}`}><span className="area-badge">{a.area}</span><b>{a.area}</b><span className="area-meta">{fmt(a.ton, 0)} Ton | {fmt(a.qty)} Unit {open ? "▾" : "›"}</span></button>
              {open && <div className="table-wrap"><table className="data-table area-table"><thead><tr><th>No</th><th>Material / 物料</th><th>Grade / 钢级</th><th>Qty / 数量</th><th>Satuan / 单位</th><th>Tonase / 吨</th></tr></thead><tbody>
                {rows.length === 0 && <tr><td colSpan="6" className="empty">Kosong / 空</td></tr>}
                {rows.map((r, i) => <tr key={i}><td>{i + 1}</td><td><b className="link" onClick={() => nav(`/stockdata?q=${r.material_code}`)}>{r.material_code}</b><br /><small>{r.spesifikasi} • {r.dimensi} • {r.panjang}</small></td><td>{r.grade_label && <span className="grade-chip">{r.grade_label}</span>}</td><td><b>{fmt(r.qty)}</b></td><td>{r.satuan}</td><td>{fmt(r.ton, 0)}</td></tr>)}
              </tbody></table></div>}
            </div>); })}
        </div>
      </div>
      <div className="dashboard-main-grid two mt">
        <div className="dashboard-card">
          <div className="dashboard-card-header"><div><h3>TONASE MASUK / KELUAR 14 HARI</h3><small>14天吨位 (Ton)</small></div></div>
          <div style={{ height: 230 }}>
            <ResponsiveContainer width="100%" height={230} minWidth={200}><BarChart data={d.chart}><CartesianGrid strokeDasharray="3 3" stroke="#edf1f5" /><XAxis dataKey="date" fontSize={10} /><YAxis fontSize={10} /><Tooltip /><Legend />
              <Bar dataKey="masuk" name="Masuk" fill="#1f9d5c" /><Bar dataKey="keluar" name="Keluar" fill="#d83e38" /></BarChart></ResponsiveContainer>
          </div>
        </div>
        <div className="dashboard-card">
          <div className="dashboard-card-header"><div><h3>STOCK PER AREA</h3><small>按区域库存</small></div></div>
          <div className="area-distribution" data-testid="dashboard-area-list">
            {d.areas.map((a) => (
              <div className="area-row" key={a.area}><b>{a.area}</b><div className="area-track"><div className="area-bar" style={{ width: `${(a.ton / maxArea) * 100}%` }} /></div><span>{fmt(a.ton)} t • {fmt(a.qty)}</span></div>
            ))}
          </div>
          <div className="dashboard-status">Diperbarui {d.generated_at.slice(11, 19)} WIB • {user.role}</div>
        </div>
      </div>
    </div>
  );
}
